<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaDeleteButton from '../base/GaiaDeleteButton.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFontWeight, gaiaFontSize, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  /** Column being filtered, e.g. "Type". */
  field: {
    type: String,
    required: true,
  },
  /** Selected value pills; empty = "Any". */
  values: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  /** "+N" overflow label after the pills, e.g. "+2". */
  overflow: {
    type: String,
    default: undefined,
  },
  /** Ink border while the value panel is open. */
  active: {
    type: Boolean,
    default: false,
  },
  /**
   * Pills wrap onto more lines instead of truncating, for a value that only
   * reads in full — a date range. The chip grows to fit them.
   */
  wrap: {
    type: Boolean,
    default: false,
  },
});

// toggle opens the value panel (compose it with GaiaPopover + checkbox rows);
// remove drops the whole chip; remove-value drops one pill.
const emit = defineEmits<{ toggle: []; remove: []; 'remove-value': [value: string] }>();

const border = gaiaNeutral.border;
const ink = gaiaNeutral.ink;
const fieldColor = gaiaNeutral.textSubtle;
const pillBg = gaiaNeutral.surfaceActive;
const surface = gaiaNeutral.surface;
const glyphHoverWash = gaiaNeutral.surfaceHoverStrong;
// The chip and its pills carry state and :hover, so their type reaches them
// through v-bind rather than GaiaText props.
const chipSize = gaiaFontSize.control;
const pillSize = gaiaFontSize.meta;
const semibold = String(gaiaFontWeight.semibold);
</script>

<template>
  <!-- Filter chip from the Entity List template: field label, selected value
       pills (or "Any"), caret, remove. Bespoke — no naive primitive. -->
  <n-flex
    inline
    align="center"
    :size="0"
    :wrap="false"
    class="gaia-filter-chip"
    :class="{ active: props.active, wrap: props.wrap }"
  >
    <button type="button" class="body" @click="emit('toggle')">
      <gaia-text tone="subtle" class="field">{{ props.field }}</gaia-text>
      <template v-if="props.values.length">
        <n-flex
          v-for="v in props.values"
          :key="v"
          inline
          align="center"
          :size="5"
          :wrap="false"
          class="pill"
        >
          <gaia-text :truncate="!props.wrap" tone="ink">{{ v }}</gaia-text>
          <n-flex
            align="center"
            justify="center"
            :size="0"
            :wrap="false"
            class="pill-x"
            role="button"
            :aria-label="`Remove ${v}`"
            @click.stop="emit('remove-value', v)"
          >
            ✕
          </n-flex>
        </n-flex>
        <gaia-text v-if="props.overflow" size="meta" weight="semibold" tone="tertiary">
          {{ props.overflow }}
        </gaia-text>
      </template>
      <gaia-text v-else weight="semibold" tone="muted">Any</gaia-text>
      <svg
        class="caret"
        :class="{ open: props.active }"
        width="11"
        height="11"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
      >
        <path d="M2.5 4.5 6 8l3.5-3.5" />
      </svg>
    </button>
    <gaia-delete-button
      intent="remove"
      size="xs"
      subject="filter"
      :style="{ marginLeft: '5px' }"
      @click="emit('remove')"
    />
  </n-flex>
</template>

<style scoped>
.gaia-filter-chip {
  height: 30px;
  padding: 0 4px 0 11px;
  border-radius: 8px;
  border: 1px solid v-bind(border);
  background: v-bind(surface);
  font-size: v-bind(chipSize);
  color: v-bind(ink);
}

.gaia-filter-chip.active {
  border-color: v-bind(ink);
}

/* A wrapping chip may shrink to its row and grows in height instead. */
.gaia-filter-chip.wrap {
  height: auto;
  min-height: 30px;
  max-width: 100%;
  padding-top: 4px;
  padding-bottom: 4px;
}

.gaia-filter-chip.wrap .body {
  min-width: 0;
  text-align: left;
}

.gaia-filter-chip.wrap .pill {
  height: auto;
  min-height: 21px;
  min-width: 0;
  max-width: none;
}

.field {
  flex: none;
  white-space: nowrap;
}

.body {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: v-bind(chipSize);
  color: inherit;
  cursor: pointer;
}

.pill {
  height: 21px;
  padding: 0 4px 0 8px;
  border-radius: 6px;
  background: v-bind(pillBg);
  font-size: v-bind(pillSize);
  font-weight: v-bind(semibold);
  max-width: 150px;
}

/* Both ✕ affordances are glyph boxes: the font-size sizes the character
   inside a fixed square, so it is a box metric and stays out of the scale. */
.pill-x {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  color: v-bind(fieldColor);
  font-size: 9.5px;
}

.pill-x:hover {
  background: v-bind(glyphHoverWash);
  color: v-bind(ink);
}

.caret {
  flex: none;
  color: v-bind(fieldColor);
  transition: transform 0.15s;
}

.caret.open {
  transform: rotate(180deg);
}
</style>
